import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {BehaviorSubject, from} from 'rxjs';
import {filter, map, reduce, startWith, switchMap, tap} from 'rxjs/operators';
import {Select} from "@ngxs/store";
import {Country, University, UniversityField} from "../../core/models";
import {Observable} from "rxjs/internal/Observable";
import {LoadUniversityFields} from "../../core/state/core.actions";


@Component({
  selector: 'app-query-filter-dialog',
  templateUrl: './query-filter-dialog.component.html',
  styleUrls: ['./query-filter-dialog.component.scss']
})
export class QueryFilterDialogComponent implements OnInit {

  ops = [
    {v: '=', name: '='},
    {v: '<', name: '⩽'},
    {v: '>', name: '⩾'},
    {v: 'IN', name: 'IN'},
    {v: 'NOT IN', name: 'NOT IN'}
  ];

  ops$ = new BehaviorSubject([]);

  vars = [
    {value: 'country__name', name: 'Страна', type: 'str'},
    {value: 'country__eaes', name: 'Страна входит в ЕАЭС', type: 'bool'},
    {value: 'country__brics', name: 'Страна входит в БРИКС', type: 'bool'},
    {value: 'name', name: 'Университет', type: 'str'},
    {value: 'reference', name: 'Референтная группа', type: 'reference'},
    {value: 'budget_usd', name: 'Общий бюджет вуза, $', type: 'num'},
    {value: 'research_budget_usd', name: 'Исследовательский бюджет вуза, $', type: 'num'},
  ];

  methods = [
    {value: 'euclidean', name: 'Евклидово расстояние'},
    {value: 'euclidean_square', name: 'Квадрат евклидова расстояния'}
  ];

  form: FormGroup;

  isBoolVar = false;
  isCatVar = false;
  isNumVar = false;
  isReference = false;
  buttonLabel = 'Добавить';

  @Select((s) => s.core.universities) universities$;
  @Select((s) => s.core.universityFields) universityFields$;
  @Select((s) => s.core.countries) countries$;
  universities = [];
  universityFields = [];
  countries = [];
  filteredValues: Observable<string[]>;
  filteredUniversities: Observable<string[]>;
  filteredUniversityFields: Observable<string[]>;

  constructor(private formBuilder: FormBuilder,
              private dialogRef: MatDialogRef<QueryFilterDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private data: any) {

    this.prepareOptions();

    this.form = this.formBuilder.group({
      'var': [data ? data.var : null , Validators.required],
      'op':  ['='],
      'val': [data ? data.val : null],
      'university': [data ? data.university : null],
      'indicators': [data ? data.indicators : null],
      'method': [data ? data.method : null],
      'indicatorsFilter': [data ? data.indicatorsFilter : null],
      'count': [data ? data.count : null],
    }, {'validators': [QueryFilterDialogComponent.validateForm]});

    this.filteredUniversities = this.form.controls.university.valueChanges.pipe(
      startWith(''),
      map(value => this._filterUniversity(value))
    );

    this.filteredUniversityFields = this.form.controls.indicatorsFilter.valueChanges.pipe(
      startWith(''),
      map(value => this._filterUniversityField(value))
    );

    this.filteredValues = this.form.controls.val.valueChanges.pipe(
      startWith(''),
      map(value => this._filterValue(value))
    );

    if(data) {
      this.buttonLabel = 'Сохранить';
      const variable = this.vars.filter((v) => v.value == data.var)[0];
      this.varSelected(variable, false);

      if(data.indicators) {
        const indicators = this.getIndicatorsNames(data.indicators);
        this.form.patchValue({
          indicators: indicators,
        });
      }

      if(data.method) {
        const method = this.methods.filter((v) => v.value == data.method)[0];
        this.form.patchValue({
          method: method,
        });
      }
    }

    this.ops$.next(this.ops);
  }

  ngOnInit() {
  }

  varSelected(evt: any, reset: boolean) {
    this.isBoolVar = evt.type === 'bool';
    this.isCatVar = evt.type === 'str';
    this.isNumVar = evt.type === 'num';
    this.isReference = evt.type === 'reference';

    if (this.isBoolVar) {
      this.ops$.next(this.ops.filter((x) => x.v === '='));
    } else if (this.isCatVar) {
      this.ops$.next(this.ops.filter((x) => !['<', '>'].includes(x.v)));
    } else if (this.isNumVar) {
      this.ops$.next(this.ops.filter((x) => x.v !== 'IN'));
    } else if (this.isReference) {
    } else {
      this.ops$.next(this.ops);
    }
    if(reset) {
      this.form.controls.val.patchValue(null);
    }

  }

  static validateForm(form: FormGroup) {
    const fields1 = ['op', 'val'];
    const fields2 = ['university', 'indicators', 'method'];
    const var_value = form.controls['var'].value;

    for(let f of fields1.concat(fields2)) {
      if (var_value === 'reference') {
        if (fields2.includes(f) && [null, ''].includes(form.controls[f].value)) {
          form.controls[f].setErrors({'incorrect': true});
          continue
        }
      } else {
        if (fields1.includes(f) && [null, ''].includes(form.controls[f].value)) {
          form.controls[f].setErrors({'incorrect': true});
          continue;
        }
      }
      form.controls[f].setErrors(null);
    }

  }

  private _filterValue(value: any): string[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
    if(this.isBoolVar) {
      return  ['true', 'false'];
    } else if(this.form.controls.var.value == 'country__name') {
      return this.countries.filter(option => option.toLowerCase().indexOf(filterValue) !== -1);
    } else if(this.form.controls.var.value == 'name') {
      return this.universities.filter(option => option.toLowerCase().indexOf(filterValue) !== -1);
    }
    return [];
  }

  private _filterUniversity(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.universities.filter(option => option.toLowerCase().indexOf(filterValue) !== -1);
  }

  private _filterUniversityField(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.universityFields.filter(option => option.toLowerCase().indexOf(filterValue) !== -1);
  }

  complete() {
    const varLabel = this.vars.filter((s: any) => s.value === this.form.value.var)[0].name;
    let payload;
    if(this.form.value.var == 'reference') {
      const valLabel = this.form.value.university;
      const paramsLabel = this.form.value.indicators.length % 10 == 1 && this.form.value.indicators.length != 11 ?
        this.form.value.indicators.length + ' параметру'
        :
        this.form.value.indicators.length + ' параметрам';

      payload = {...this.form.value, label: `${varLabel} ${valLabel} по ${paramsLabel}`};
      payload.method = typeof payload.method === 'object' ? payload.method.value : payload.method;
      payload.indicators = this.getIndicatorsValues(this.form.value.indicators);
      delete(payload.indicatorsFilter);
    } else {
      const valLabel = typeof this.form.value.val === 'object' ? this.form.value.val.name : this.form.value.val;
      const op = this.ops.filter(x => x.v == this.form.value.op)[0].name;
      payload = {...this.form.value, label: `${varLabel} ${op} ${valLabel}`};
      payload.val = typeof payload.val === 'object' ? payload.val.name : payload.val;
      payload.val = payload.val === 'true' ? true : payload.val;
      payload.val = payload.val === 'false' ? false : payload.val;

    }

    this.dialogRef.close({data: payload});
  }

  onIndicatorsPanelClose() {

  }

  prepareOptions() {
    this.universities$
      .pipe(
        switchMap((v: Array<University>) => from(v)
          .pipe(
            map((v: University) => v.name),
            reduce( (acc, v) => acc.concat(v), []),
          )
        )
      )
      .subscribe(v => this.universities = v);

    this.countries$
      .pipe(
        switchMap((v: Array<Country>) => from(v)
          .pipe(
            map((v: Country) => v.name),
            reduce( (acc, v) => acc.concat(v), []),
          )
        )
      )
      .subscribe(v => this.countries = v);

    this.universityFields$
      .pipe(
        switchMap((v: Array<UniversityField>) => from(v)
          .pipe(
            filter( (v: UniversityField) => ['int', 'float'].includes(v.type)),
            map((v: UniversityField) => v.name),
            reduce( (acc, v) => acc.concat(v), []),
          )
        )
      )
      .subscribe(v => this.universityFields = v);
  }

  getIndicatorsValues(names: Array<string>) {
    let values = [];

    this.universityFields$
    .pipe(
      switchMap((v: Array<UniversityField>) => from(v)
        .pipe(
          filter( (v: UniversityField) => names.includes(v.name)),
          map((v: UniversityField) => v.value),
          reduce( (acc, v) => acc.concat(v), []),
        )
      )
    )
    .subscribe(v => values = v);
    return values;
  }

  getIndicatorsNames(values: Array<string>) {
    let names = [];

    this.universityFields$
    .pipe(
      switchMap((v: Array<UniversityField>) => from(v)
        .pipe(
          filter( (v: UniversityField) => values.includes(v.value)),
          map((v: UniversityField) => v.name),
          reduce( (acc, v) => acc.concat(v), []),
        )
      )
    )
    .subscribe(v => names = v);
    return names;
  }

}
